import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { defaultMarket } from "../data/markets";
import { clearUrlCountry, countryFromUrl, detectCountry, diagnosticsEnabled, formatAmount, marketForCountry, marketForPreference, readPreference, storePreference, sumAmounts } from "../utils/prices";
import type { CountryDetection, CountryPreference, CountrySource, Market } from "../types/market";
export type LocalizedPrices = {
  market: Market;
  /** País cuyos precios se muestran (null = internacional USD). */
  country: string | null;
  /** Origen del país mostrado. */
  source: CountrySource;
  /** Resultado de la detección automática, aunque no esté en uso. */
  detection: CountryDetection;
  /** País forzado por ?pais=XX (solo pruebas). */
  urlOverride: string | null;
  preference: CountryPreference;
  setPreference: (preference: CountryPreference) => void;
  showDiagnostics: boolean;
  /** Precio del producto principal, ya formateado (p. ej. "USD 19.99"). */
  mainPrice: string;
  /** Valores de referencia de los 4 bonos, formateados, en orden. */
  bonusPrices: string[];
  /** Suma de los 4 bonos en moneda local, formateada. */
  bonusesTotal: string;
};
export const LocalizedPricesContext = createContext<LocalizedPrices | null>(null);
export function LocalizedPricesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Prioridad: ?pais=XX (pruebas) → selección guardada → detección automática → USD.
  const [urlOverride, setUrlOverride] = useState<string | null>(() => countryFromUrl());
  const [preference, setPreferenceState] = useState<CountryPreference>(() => readPreference());
  const [detection, setDetection] = useState<CountryDetection>({
    country: null,
    source: "pending"
  });
  const [showDiagnostics] = useState<boolean>(() => diagnosticsEnabled());
  useEffect(() => {
    const controller = new AbortController();
    detectCountry(controller.signal).then(result => {
      if (!controller.signal.aborted) setDetection(result);
    });
    return () => controller.abort();
  }, []);
  const setPreference = useCallback((next: CountryPreference) => {
    storePreference(next);
    clearUrlCountry();
    setUrlOverride(null);
    setPreferenceState(next);
  }, []);

  // Todas las cifras se derivan de un único cálculo: cambian juntas en un solo render.
  const value = useMemo<LocalizedPrices>(() => {
    let market: Market;
    let country: string | null;
    let source: CountrySource;
    if (urlOverride) {
      market = marketForCountry(urlOverride);
      country = urlOverride;
      source = "override";
    } else if (preference !== "auto") {
      market = marketForPreference(preference);
      country = preference === "INTL" ? null : preference;
      source = "manual";
    } else {
      market = detection.source === "pending" ? defaultMarket : marketForCountry(detection.country);
      country = detection.country;
      source = detection.source;
    }
    return {
      market,
      country,
      source,
      detection,
      urlOverride,
      preference,
      setPreference,
      showDiagnostics,
      mainPrice: formatAmount(market.main, market),
      bonusPrices: market.bonuses.map(amount => formatAmount(amount, market)),
      bonusesTotal: formatAmount(sumAmounts(market.bonuses), market)
    };
  }, [urlOverride, preference, detection, setPreference, showDiagnostics]);
  return <LocalizedPricesContext.Provider value={value} data-id="element-333" data-mp="exhusc56xhat">{children}</LocalizedPricesContext.Provider>;
}